import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Eye, EyeOff, Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function OfficialResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [official, setOfficial] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/official/reset-password/${token}`);
        setOfficial(data);
        setValidToken(true);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || "Lien invalide ou expiré");
      } finally {
        setChecking(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Mot de passe trop court (min. 8 caractères).");
    if (password !== confirm) return setError("Les deux mots de passe ne correspondent pas.");
    setLoading(true);
    try {
      await api.post("/auth/official/reset-password", { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate("/officiel/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-6" data-testid="official-reset-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <Logo size={40} />
          <span className="ml-2 badge-dark">Officiel</span>
        </div>
        <div className="card-flat">
          <div className="label mb-1">Réinitialisation</div>
          <h2 className="font-serif text-4xl mb-2">Nouveau mot de passe.</h2>

          {checking ? (
            <p className="text-sm text-[var(--muted)] mt-4 font-mono uppercase tracking-widest text-[10px]">Vérification du lien…</p>
          ) : !validToken ? (
            <>
              <div className="border-2 border-black p-3 text-xs bg-conflict-stripes-light font-mono mt-4" data-testid="official-reset-invalid">
                {error}
              </div>
              <Link to="/officiel/login" className="btn-secondary w-full justify-center mt-4">
                Retour à la connexion
              </Link>
            </>
          ) : success ? (
            <div className="mt-4 space-y-3" data-testid="official-reset-success">
              <div className="border-2 border-black p-3 text-xs flex items-start gap-2">
                <CheckCircle2 size={16} strokeWidth={2.5} className="shrink-0 mt-0.5" />
                <span>Mot de passe modifié avec succès. Redirection vers la connexion…</span>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-[var(--muted)] mb-6 leading-relaxed">
                Bonjour {official?.first_name} {official?.last_name}. Choisis un nouveau mot de passe (8 caractères minimum).
              </p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="label block mb-2">Nouveau mot de passe</label>
                  <div className="relative">
                    <input
                      type={show ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      autoFocus
                      className="input-line pr-10"
                      data-testid="official-reset-password-input"
                    />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-0 top-2 text-[var(--muted)] hover:text-black">
                      {show ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="label block mb-2">Confirmer le mot de passe</label>
                  <input
                    type={show ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    disabled={loading}
                    className="input-line"
                    data-testid="official-reset-confirm-input"
                  />
                </div>
                {error && (
                  <div className="border-2 border-black p-3 text-xs bg-conflict-stripes-light font-mono" data-testid="official-reset-error">
                    {error}
                  </div>
                )}
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="official-reset-submit-btn">
                  <Lock size={14} strokeWidth={2.5} />
                  {loading ? "Enregistrement…" : "Confirmer le nouveau mot de passe"}
                </button>
              </form>
            </>
          )}
        </div>
        <div className="mt-4 text-center">
          <Link to="/officiel/login" className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black inline-flex items-center gap-1">
            Retour à la connexion <ArrowRight size={11} strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </div>
  );
}
